import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker V2 - Homepage Featured Product Selector Integration
 * @docs https://integrate.skio.com/
 *
 * Extends the original Skio Plan Picker with custom styling for the homepage featured product selector
 */

export class SkioPlanPickerV2 extends LitElement {
  static properties = {
    key: { type: String },
    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },
    options: { type: Object },
    moneyFormat: { type: String },
    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },
    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },
    disableUrl: { type: Boolean },
    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
        display: block;
      }

      /* Hide default styles - we'll use the page's existing styles */
      .skio-plan-picker-v2 {
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
        border: 0;
        font-family: inherit;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      /* Exact match to original Tailwind classes */
      .subscription-option {
        background: white;
        border: 1px solid #333333;
        border-radius: 1rem;
        padding: 1rem;
        margin-bottom: 20px;
        transition: all 0.2s ease;
        cursor: pointer;
        position: relative;
        display: block;
      }

      .subscription-option.selected {
        background: #f8f9fa;
        border-color: #dc2626;
      }

      .subscription-option input[type="radio"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .option-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }

      .option-left {
        flex: 1;
      }

      .option-title {
        font-size: 1.125rem;
        font-weight: 700;
        color: #333333;
        margin-bottom: 0.25rem;
      }

      .option-price {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        margin-bottom: 0;
      }

      .price-current {
        font-size: 1.5rem;
        font-weight: 700;
        color: #333333;
      }

      .price-compare {
        font-size: 1.125rem;
        color: #6b7280;
        text-decoration: line-through;
      }

      .option-description {
        font-size: 0.875rem;
        color: #333333;
        margin: 0;
      }

      .subscription-benefits {
        margin-top: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .benefit-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .benefit-check {
        color: #16a34a;
        font-size: 1.125rem;
        font-weight: 700;
      }

      .benefit-text {
        font-size: 0.875rem;
        font-weight: 500;
        color: #333333;
      }

      .discount-badge {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: #dc2626;
        color: white;
        font-size: 0.875rem;
        font-weight: 700;
        padding: 0.5rem 0.75rem;
        border-radius: 9999px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }

      .onetime-warning {
        margin-top: 1rem;
        display: none;
      }

      .onetime-warning.show {
        display: block;
      }

      .warning-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .warning-icon {
        color: #dc2626;
        font-size: 1.125rem;
        font-weight: 700;
      }

      .frequency-selector {
        margin-top: 0.75rem;
      }

      .frequency-label {
        color: #666666;
        font-weight: 500;
        font-size: 0.875rem;
        margin-bottom: 0.25rem;
        display: block;
      }

      .frequency-dropdown {
        width: 100%;
        padding: 0.5rem 2rem 0.5rem 0.75rem;
        background: #f7f7f7;
        border: 1px solid #ccc;
        border-radius: 0.25rem;
        font-size: 0.875rem;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.75rem center;
        background-size: 1rem;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode V2';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }
    `,
  ]

  constructor() {
    super()

    // Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted V2')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return

    return html`
      ${this.debug ? this.setupMode() : null}

      <div class="skio-plan-picker-v2">
        ${this.renderSubscriptionOptions()}
        ${this.renderOnetimeOption()}
      </div>
    `
  }

  renderSubscriptionOptions() {
    if (!this.availableSellingPlanGroups?.length) return ''

    return this.availableSellingPlanGroups.map((group, index) => html`
      <label class="subscription-option ${this.selectedSellingPlanGroup == group ? 'selected' : ''}"
             @click=${() => this.selectSellingPlanGroup(group)}>
        <input
          type="radio"
          name="purchase_option_skio"
          value="subscription"
          ?checked=${this.selectedSellingPlanGroup == group}
          @change=${() => this.selectSellingPlanGroup(group)}
        />

        ${this.discountText(group.selected_selling_plan) ? html`
          <div class="discount-badge">
            ${this.discountText(group.selected_selling_plan)}
          </div>
        ` : ''}

        <div class="option-content">
          <div class="option-left">
            <h4 class="option-title">
              ${group.name !== 'Prepaid' ? 'Autoship & Save' : group.name}
            </h4>
            <div class="option-price">
              <span class="price-current">
                ${this.price(group.selected_selling_plan)}
              </span>
              ${this.selectedVariant?.compare_at_price > this.selectedVariant.price ||
                this.selectedVariant.price > this.price(group.selected_selling_plan, false) ? html`
                <span class="price-compare">
                  ${this.money(this.selectedVariant.compare_at_price || this.selectedVariant.price)}
                </span>
              ` : ''}
            </div>
            ${this.getAvailableSellingPlans(group).length > 1 ? html`
              <div class="frequency-selector">
                <label class="frequency-label">Delivery frequency</label>
                <select class="frequency-dropdown" @change=${e => this.selectSellingPlan(e.target.value)}>
                  ${this.getAvailableSellingPlans(group).map(plan => html`
                    <option value="${plan.id}" ?selected=${group.selected_selling_plan.id === plan.id}>
                      ${plan.name}
                    </option>
                  `)}
                </select>
              </div>
            ` : ''}
          </div>
        </div>

          ${this.selectedSellingPlanGroup == group ? html`
          <div class="subscription-benefits">
            <div class="benefit-item">
              <span class="benefit-check">✓</span>
              <span class="benefit-text">${this.options?.benefit_1 || 'FREE express shipping'}</span>
            </div>
            <div class="benefit-item">
              <span class="benefit-check">✓</span>
              <span class="benefit-text">${this.options?.benefit_2 || 'Pause or cancel anytime'}</span>
            </div>
            <div class="benefit-item">
              <span class="benefit-check">✓</span>
              <span class="benefit-text">${this.options?.benefit_3 || '30-day money back guarantee'}</span>
            </div>
          </div>


        ` : ''}
      </label>
    `)
  }

  renderOnetimeOption() {
    if (this.product.requires_selling_plan) return ''

    return html`
      <label class="subscription-option ${!this.selectedSellingPlanGroup ? 'selected' : ''}"
             @click=${() => this.selectSellingPlanGroup(null)}>
        <input
          type="radio"
          name="purchase_option_skio"
          value="onetime"
          ?checked=${!this.selectedSellingPlanGroup}
          @change=${() => this.selectSellingPlanGroup(null)}
        />

        <div class="option-content">
          <div class="option-left">
            <h4 class="option-title">One-Time Purchase</h4>
            <div class="option-price">
              <span class="price-current">
                ${this.money(this.selectedVariant.price)}
              </span>
              ${this.selectedVariant?.compare_at_price > this.selectedVariant.price ? html`
                <span class="price-compare">
                  ${this.money(this.selectedVariant.compare_at_price)}
                </span>
              ` : ''}
            </div>
          </div>
        </div>

        ${!this.selectedSellingPlanGroup ? html`
          <div class="onetime-warning show">
            <div class="warning-item">
              <span class="warning-icon">❌</span>
              <span class="benefit-text">${this.options?.negative || 'No FREE shipping'}</span>
            </div>
          </div>
        ` : ''}
      </label>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker V2 - Homepage Integration
        <small>This will not appear on the live site.</small>
        <small>Key: ${this.key}</small>
        <div>Product: ${this.product ? '✓' : '❌'}</div>
        <div>Selected Variant: ${this.selectedVariant ? '✓' : '❌'}</div>
        <div>Form: ${this.form ? '✓' : '❌'}</div>
        <div>Variant Input: ${this.variantInput ? '✓' : '❌'}</div>
      </div>
    `
  }

  // Copy all the utility methods from the original component
  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    // Find the form - look for the featured product selector form
    this.form = document.querySelector('[data-product-form-element]') ||
               document.querySelector(`#${this.options?.form_id}`) ||
               this.findProductForm()

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput) this.variantInput = document.querySelector(`[name="id"][form="${this.options?.form_id}"]`)

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')
    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    // Listen for variant changes from the main form
    let skio = this
    document.addEventListener('variantChanged', function (e) {
      let variantId = e.detail.variantId
      let variant = skio.product.variants.find(x => x.id == variantId)
      if (variant) skio.selectedVariant = variant
      else skio.log('Unable to find variant with id: ', variantId)
      this.variantChanged = true
      skio.requestUpdate()
    })
  }

  findProductForm() {
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))

      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)
          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan = availableSellingPlans[0]

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan
          group.selected = !!urlSelectedSellingPlan || !!nameSelectedSellingPlan
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  log(...args) {
    args.unshift(`%c[skio plan picker v2][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker v2][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker-v2 component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker-v2 component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return ''
    } else {
      return `${discount.percent}% off`
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1
    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]
        return product
      })
  }
}

if (!customElements.get('skio-plan-picker-v2')) {
  customElements.define('skio-plan-picker-v2', SkioPlanPickerV2)
}

if (window) window.SkioPlanPickerV2 = SkioPlanPickerV2
